import usePermissions from '@/hooks/usePermissions';
import { getLandingPath, mayOpenSurface } from '@/utils/permissionRegistry';
import { getEncryptedClientId } from '@/utils/helper';
import { canReachInsightsPage } from '@/utils/checkAccess';
import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

interface InsightsRouteProps {
  /** What this page needs. All of them must be held. Required by design. */
  permission: string | string[];
  children: ReactNode;
}

/**
 * Route guard for the Insights section. Identical to AdminRoute except for
 * the legacy fallback it applies to built-in roles: Insights is not an
 * administrative area, so the built-in User reaches it too — see
 * canReachInsightsPage() for why AdminRoute's legacyAdmin() is wrong here.
 *
 * Subscribes to the permission slice so it re-evaluates once /roles/me
 * resolves — canReachInsightsPage() reads a non-reactive registry and would
 * otherwise decide before permissions exist.
 */
/**
 * Where to send someone who cannot open the page they asked for: the first
 * screen their own permissions do reach. Falling back to '/' bounced them to
 * a landing page they often could not open either.
 */
const deniedPath = (): string => {
  const path = getLandingPath();
  if (!path) return '/';
  const client = getEncryptedClientId();
  return client ? `/${client}${path}` : path;
};

const InsightsRoute = ({ permission, children }: InsightsRouteProps) => {
  const { isLoading, isReady } = usePermissions();
  const location = useLocation();

  // The bypass exists for running the UI against no backend, where there is no
  // permission set at all and every route would block. Scoped to exactly that:
  // once /roles/me has answered, dev enforces like production, so role changes
  // are testable locally.
  if (import.meta.env.DEV && !isReady) return <>{children}</>;

  // Hold the frame rather than redirect on an unresolved permission set.
  if (isLoading) return null;

  // A custom role is measured against the screens it was granted, not the keys
  // it happens to hold: Overview's closure carries branches:read and
  // regions:read, which is the whole of the Locations requirement, so no
  // key-based test can refuse it. mayOpenSurface returns null for anything
  // that is not a screen, and the permission check below decides those.
  const granted = mayOpenSurface(location.pathname);
  if (granted === false) return <Navigate to={deniedPath()} replace />;

  const required = Array.isArray(permission) ? permission : [permission];
  return canReachInsightsPage(...required) ? <>{children}</> : <Navigate to={deniedPath()} replace />;
};

export default InsightsRoute;
